// useSuggestionSettings — deck-lokale Einstellungen der Vorschläge: ausgeblendete Karten
// und stummgeschaltete Themen. Jede Mutation antwortet mit dem vollständigen Stand, der
// direkt in den Cache geht; die Vorschlagsliste rechnet danach neu.
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { json } from '../../../api/apiClient';
import { DECK_SUGGESTIONS_ROOT_KEY, useDropSuggestionFromCache } from './useDeckSuggestions';
import { announceDeckChanged } from '../hooks/useDeckSyncChannel';
import { deckUiId } from '../utils/deckIds';

/** Wurzel-Key der Einstellungs-Abfrage. */
export const SUGGESTION_SETTINGS_ROOT_KEY = 'deck-suggestion-settings';

export function suggestionSettingsQueryKey(deckId) {
  return [SUGGESTION_SETTINGS_ROOT_KEY, deckId ?? null];
}

const LEERE_SICHT = { hiddenOracleIds: [], mutedTagIds: [], hiddenCards: [], mutedTags: [] };

/**
 * Einstellungen eines Decks samt Mutationen.
 *
 *   const { hiddenCards, mutedTags, hideCard, unhideCard, muteTag, unmuteTag } =
 *     useSuggestionSettings(deckId);
 *
 * `hidingOracleIds` ist die Menge der Karten, deren Ausblenden gerade läuft — die Liste
 * zeigt daran ihre Lade-Rückmeldung, auch bei mehreren ✕ kurz hintereinander.
 *
 * Fehler meldet der globale Mutations-Toast (`queryClient.js`) — die Mutationen fangen
 * nichts ab und tragen kein `meta.handlesError`.
 */
export function useSuggestionSettings(deckId) {
  const queryClient = useQueryClient();
  const vorschlagEntnehmen = useDropSuggestionFromCache(deckId);

  const query = useQuery({
    queryKey: suggestionSettingsQueryKey(deckId),
    queryFn: ({ signal }) => json(`/api/decks/${deckId}/suggestion-settings`, { signal }),
    enabled: deckId != null,
  });

  // Die Antwort einer Mutation ist der vollständige Stand und geht sofort in den Cache,
  // damit der Chip ohne Zwischenschritt umspringt. Zusätzlich wird die Einstellungs-Abfrage
  // invalidiert: Zwei schnelle Klicks laufen parallel, und landet die ältere Antwort
  // zuletzt, zeigte die Oberfläche sonst dauerhaft den überholten Stand. Ein anderes
  // Fenster auf demselben Deck zeigt die ausgeblendete Karte weiter, bis die Meldung
  // über den Fenster-Abgleich dort ankommt.
  const übernehmen = useCallback((sicht) => {
    queryClient.setQueryData(suggestionSettingsQueryKey(deckId), sicht);
    queryClient.invalidateQueries({ queryKey: suggestionSettingsQueryKey(deckId) });
    queryClient.invalidateQueries({ queryKey: [DECK_SUGGESTIONS_ROOT_KEY] });
    announceDeckChanged(deckUiId(deckId));
  }, [queryClient, deckId]);

  // Mehrere ✕ laufen gleichzeitig: eine Menge statt einer einzelnen Kennung. `variables`
  // der Mutation trägt immer nur den letzten Aufruf — der Spinner der ersten Kachel wäre
  // beim zweiten Klick erloschen, obwohl ihr Server-Aufruf noch läuft.
  const [hidingOracleIds, setHidingOracleIds] = useState(() => new Set());

  // Die Kachel geht mit der Bestätigung aus der Liste, nicht erst mit der Antwort des
  // Refetchs — der scort den ganzen Kandidatenkreis neu und braucht dafür spürbar Zeit.
  const hideMutation = useMutation({
    mutationFn: (oracleId) => json(`/api/decks/${deckId}/suggestion-hidden`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oracleId }),
    }),
    onMutate: (oracleId) => {
      setHidingOracleIds((laufend) => new Set(laufend).add(oracleId));
    },
    onSuccess: (sicht, oracleId) => {
      vorschlagEntnehmen({ oracleId });
      übernehmen(sicht);
    },
    onSettled: (_daten, _fehler, oracleId) => {
      setHidingOracleIds((laufend) => {
        const übrig = new Set(laufend);
        übrig.delete(oracleId);
        return übrig;
      });
    },
  });

  const unhideMutation = useMutation({
    mutationFn: (oracleId) => json(
      `/api/decks/${deckId}/suggestion-hidden?oracleId=${encodeURIComponent(oracleId)}`,
      { method: 'DELETE' },
    ),
    onSuccess: übernehmen,
  });

  const muteMutation = useMutation({
    mutationFn: (tagId) => json(`/api/decks/${deckId}/suggestion-muted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tagId }),
    }),
    onSuccess: übernehmen,
  });

  const unmuteMutation = useMutation({
    mutationFn: (tagId) => json(
      `/api/decks/${deckId}/suggestion-muted?tagId=${encodeURIComponent(tagId)}`,
      { method: 'DELETE' },
    ),
    onSuccess: übernehmen,
  });

  const sicht = query.data || LEERE_SICHT;

  return {
    hiddenOracleIds: sicht.hiddenOracleIds || [],
    mutedTagIds: sicht.mutedTagIds || [],
    hiddenCards: sicht.hiddenCards || [],
    mutedTags: sicht.mutedTags || [],
    status: query.status,
    hideCard: hideMutation.mutate,
    hidingOracleIds,
    unhideCard: unhideMutation.mutate,
    muteTag: muteMutation.mutate,
    unmuteTag: unmuteMutation.mutate,
  };
}

export default useSuggestionSettings;
